import { useCallback, useEffect, useState } from "react";
import { validateTemplateLayout } from "@/lib/template-layout/validateTemplateLayout";
import { resolveTemplateLayoutRepositorySelection } from "@/lib/template-studio/repository/getTemplateLayoutRepository";
import type {
  TemplateLayoutRepository,
  TemplateLayoutRepositoryContext,
  TemplateLayoutRepositoryError,
} from "@/lib/template-studio/repository/templateLayoutRepository.types";
import { templateStudioRendererComponentTypes } from "@/lib/template-studio/templateStudioRendererRegistry";
import useAuth, { type AuthState } from "@/store/auth.slice";
import type {
  TemplateLayout,
  TemplateLayoutDocument,
} from "@/types/template-layout/templateLayout.types";

export const TEMPLATE_LAYOUT_REQUEST_ERROR_MESSAGE =
  "This template couldn't be loaded. Please try again.";
export const TEMPLATE_LAYOUT_INVALID_DOCUMENT_ERROR_MESSAGE =
  "This template can't be previewed because its document is invalid.";

export interface TemplateLayoutError {
  kind: "not-found" | "invalid-document" | "request";
  message: string;
}

export interface UseTemplateLayoutOptions {
  repository?: TemplateLayoutRepository;
  context?: TemplateLayoutRepositoryContext;
}

export interface UseTemplateLayoutReturn {
  template: TemplateLayout | null;
  isLoading: boolean;
  error: TemplateLayoutError | null;
  isRetryable: boolean;
  retry: () => void;
}

const supportedComponentTypes = new Set(templateStudioRendererComponentTypes);

const hasUnsupportedComponents = (document: TemplateLayoutDocument): boolean =>
  Object.values(document.spec.elements).some(
    ({ type }) => !supportedComponentTypes.has(type),
  );

const mapRepositoryError = (
  error: TemplateLayoutRepositoryError,
): TemplateLayoutError => {
  if (error.code === "NOT_FOUND") {
    return { kind: "not-found", message: error.message };
  }
  if (error.code === "INVALID_DOCUMENT") {
    return { kind: "invalid-document", message: error.message };
  }
  return { kind: "request", message: TEMPLATE_LAYOUT_REQUEST_ERROR_MESSAGE };
};

/** Loads one saved template layout, including its persisted document. */
export const useTemplateLayout = (
  templateId: string,
  {
    repository: repositoryOverride,
    context: contextOverride,
  }: UseTemplateLayoutOptions = {},
): UseTemplateLayoutReturn => {
  const userId = useAuth((state: AuthState) => state.user.id);
  const tenantId = useAuth((state: AuthState) => state.user.default_tenant.id);
  const [template, setTemplate] = useState<TemplateLayout | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<TemplateLayoutError | null>(null);
  const [fetchGeneration, setFetchGeneration] = useState(0);

  const retry = useCallback(() => {
    if (error?.kind !== "request") return;
    setFetchGeneration((generation) => generation + 1);
  }, [error]);

  useEffect(() => {
    let cancelled = false;

    const finishWithError = (nextError: TemplateLayoutError) => {
      if (cancelled) return;
      setTemplate(null);
      setError(nextError);
      setIsLoading(false);
    };

    const load = async () => {
      setTemplate(null);
      setError(null);
      setIsLoading(true);

      const selection = resolveTemplateLayoutRepositorySelection({
        user: { id: userId, default_tenant: { id: tenantId } },
        repository: repositoryOverride,
        context: contextOverride,
      });

      try {
        const result = await selection.repository.get(
          selection.context,
          templateId,
        );
        if (cancelled) return;

        if (!result.success) {
          finishWithError(mapRepositoryError(result.error));
          return;
        }

        const document = validateTemplateLayout(result.data.document);
        if (!document.success || hasUnsupportedComponents(document.data)) {
          finishWithError({
            kind: "invalid-document",
            message: TEMPLATE_LAYOUT_INVALID_DOCUMENT_ERROR_MESSAGE,
          });
          return;
        }

        setTemplate({ ...result.data, document: document.data });
        setError(null);
        setIsLoading(false);
      } catch {
        finishWithError({
          kind: "request",
          message: TEMPLATE_LAYOUT_REQUEST_ERROR_MESSAGE,
        });
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [
    contextOverride,
    fetchGeneration,
    repositoryOverride,
    templateId,
    tenantId,
    userId,
  ]);

  return {
    template,
    isLoading,
    error,
    isRetryable: error?.kind === "request",
    retry,
  };
};
